<template>
  <v-dialog :model-value="modelValue" max-width="760" scrollable persistent @update:model-value="cancel">
    <v-card v-if="status">
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2" :color="operation === 'delete' ? 'error' : 'primary'">
          {{ operation === 'delete' ? 'mdi-delete-alert' : operation === 'changeId' ? 'mdi-identifier' : 'mdi-content-copy' }}
        </v-icon>
        {{ title }}
      </v-card-title>

      <v-card-text style="max-height: 66vh;">
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 8px;">
          <span class="status-swatch" :style="{ background: status.color || '#BDBDBD' }"></span>
          <span class="text-body-1">{{ status.icon }} {{ status.descr }}</span>
          <v-chip size="small" label>id {{ status.id }}</v-chip>
          <v-chip size="small" label variant="tonal">{{ typeDescr }}</v-chip>
        </div>

        <!-- Nuovo id per cambio e duplicazione -->
        <v-text-field
          v-if="operation !== 'delete'"
          v-model.number="newId"
          :label="t('admConfig.statusEditor.impact.newId')"
          type="number"
          density="compact"
          :error="!!newIdError"
          :error-messages="newIdError"
          class="mb-3"
          style="max-width: 220px;"
        />

        <!-- 1. Processi esistenti -->
        <div class="text-subtitle-2 mb-1">{{ t('admConfig.statusEditor.impact.projectsTitle') }}</div>
        <v-alert
          v-if="counts.active > 0"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          <div>{{ t('admConfig.statusEditor.impact.projectsActive', { count: counts.active }) }}</div>
          <div class="text-body-2 mt-1">
            {{ operation === 'delete'
              ? t('admConfig.statusEditor.impact.projectsOnDelete')
              : t('admConfig.statusEditor.impact.projectsOnChangeId', { id: status.id }) }}
          </div>
          <div v-if="counts.archived" class="text-caption mt-1">
            {{ t('admConfig.statusEditor.impact.projectsArchived', { count: counts.archived }) }}
          </div>
        </v-alert>
        <v-alert v-else type="success" variant="tonal" density="compact" class="mb-3">
          {{ t('admConfig.statusEditor.impact.projectsNone') }}
          <span v-if="counts.archived">
            {{ t('admConfig.statusEditor.impact.projectsArchived', { count: counts.archived }) }}
          </span>
        </v-alert>

        <template v-if="operation !== 'duplicate'">
          <!-- 2. Altri stati che vi puntano -->
          <div class="text-subtitle-2 mb-1">{{ t('admConfig.statusEditor.impact.nextStatesTitle') }}</div>
          <div v-if="referrers.length" class="mb-2">
            <v-chip v-for="s in referrers" :key="s.id" size="small" label variant="tonal" class="mr-1 mb-1">
              {{ s.icon }} {{ s.descr }} ({{ s.id }})
            </v-chip>
            <v-checkbox
              v-model="options.updateNextStates"
              :label="operation === 'delete'
                ? t('admConfig.statusEditor.impact.removeNextStates')
                : t('admConfig.statusEditor.impact.updateNextStates')"
              density="compact"
              hide-details
              color="primary"
            />
            <div v-if="operation === 'delete' && !options.updateNextStates" class="text-caption text-error mt-1">
              {{ t('admConfig.statusEditor.impact.nextStatesBlocking') }}
            </div>
          </div>
          <div v-else class="text-body-2 text-medium-emphasis mb-3">
            {{ t('admConfig.statusEditor.impact.nextStatesNone') }}
          </div>

          <!-- 3-4. Definizione campi del tipo -->
          <div class="text-subtitle-2 mb-1">{{ t('admConfig.statusEditor.impact.fieldsTitle') }}</div>
          <div v-if="fieldRefs.length" class="mb-2">
            <div v-for="fieldRef in fieldRefs" :key="`${fieldRef.kind}-${fieldRef.name}`" class="d-flex align-center flex-wrap mb-1" style="gap: 6px;">
              <v-chip size="x-small" label variant="tonal" :color="fieldRef.kind === 'validation' ? 'deep-orange' : 'blue-grey'">
                {{ fieldRef.kind === 'validation'
                  ? t('admConfig.statusEditor.impact.kindValidation')
                  : t('admConfig.statusEditor.impact.kindEditable') }}
              </v-chip>
              <span class="text-body-2">{{ fieldRef.label }}</span>
              <code class="text-caption text-medium-emphasis">{{ fieldRef.name }}</code>
            </div>
            <v-checkbox
              v-model="options.updateFields"
              :label="t('admConfig.statusEditor.impact.updateFields')"
              :hint="t('admConfig.statusEditor.impact.updateFieldsHint')"
              density="compact"
              persistent-hint
              color="primary"
            />
          </div>
          <div v-else class="text-body-2 text-medium-emphasis mb-3">
            {{ t('admConfig.statusEditor.impact.fieldsNone') }}
          </div>
        </template>

        <!-- 5. instructions.json -->
        <div class="text-subtitle-2 mb-1">{{ t('admConfig.statusEditor.impact.instructionsTitle') }}</div>
        <div class="mb-2">
          <div class="text-body-2">
            <code>{{ status.instructionKey || '—' }}</code>
            <span class="text-medium-emphasis ml-2">
              {{ instructionInfo.exists
                ? t('admConfig.statusEditor.impact.instructionsPresent', { chars: instructionInfo.length })
                : t('admConfig.statusEditor.impact.instructionsAbsent') }}
            </span>
          </div>
          <v-checkbox
            v-if="instructionInfo.exists"
            v-model="instructionsOption"
            :label="instructionsCheckboxLabel"
            density="compact"
            hide-details
            color="primary"
          />
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="cancel(false)">{{ t('admConfig.statusEditor.cancel') }}</v-btn>
        <v-btn
          variant="flat"
          :color="operation === 'delete' ? 'error' : 'primary'"
          :disabled="!!newIdError"
          @click="confirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  operation: { type: String, default: 'delete' },
  status: { type: Object, default: null },
  projectType: { type: Number, default: 0 },
  typeDescr: { type: String, default: '' },
  workingTypes: { type: Array, default: () => [] },
  workingInstructions: { type: Object, default: () => ({}) },
  fields: { type: Array, default: () => [] },
  statusCounts: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const newId = ref(0)
const options = ref({ updateNextStates: true, updateFields: false })
// Rinomina (cambio id), eliminazione o copia della chiave istruzioni, a seconda dell'operazione
const instructionsOption = ref(false)

const activeType = computed(() => props.workingTypes.find(type => type.id === props.projectType) || null)
const statusList = computed(() => activeType.value?.statusList || [])

const title = computed(() => {
  if (props.operation === 'delete') return t('admConfig.statusEditor.impact.titleDelete')
  if (props.operation === 'changeId') return t('admConfig.statusEditor.impact.titleChangeId')
  return t('admConfig.statusEditor.impact.titleDuplicate')
})

const confirmLabel = computed(() => {
  if (props.operation === 'delete') return t('admConfig.statusEditor.delete')
  if (props.operation === 'changeId') return t('admConfig.statusEditor.changeId')
  return t('admConfig.statusEditor.duplicate')
})

const instructionsCheckboxLabel = computed(() => {
  if (props.operation === 'delete') return t('admConfig.statusEditor.impact.deleteInstructions')
  if (props.operation === 'changeId') return t('admConfig.statusEditor.impact.renameInstructions')
  return t('admConfig.statusEditor.impact.copyInstructions')
})

// Conteggio dei processi realmente fermi su questo stato (colonne calcolate del DB)
const counts = computed(() => {
  const row = props.statusCounts.find(
    r => r.projectType === props.projectType && String(r.status) === String(props.status?.id)
  )
  return { active: row?.active || 0, archived: row?.archived || 0 }
})

// Altri stati che elencano questo id fra i nextStates: solo nello stesso tipo, perché gli id
// si ripetono fra tipi diversi (0, 2 e 99 esistono in più tipi)
const referrers = computed(() => statusList.value.filter(s =>
  Number(s.id) !== Number(props.status?.id) && (s.nextStates || []).some(n => Number(n) === Number(props.status?.id))
))

// Campi che nominano questo stato. Le chiavi sono stringhe. La prop arriva già ristretta al
// tipo di processo attivo: i campi vivono in un file per tipo, non c'è più nulla da filtrare.
const fieldRefs = computed(() => {
  const key = String(props.status?.id)
  const refs = []
  props.fields.forEach(field => {
    if (field.validationByStatus && field.validationByStatus[key] !== undefined) {
      refs.push({ kind: 'validation', name: field.name, label: field.label || field.name })
    }
    if ((field.editableInStatuses || []).some(v => String(v) === key)) {
      refs.push({ kind: 'editable', name: field.name, label: field.label || field.name })
    }
  })
  return refs
})

const instructionInfo = computed(() => {
  const key = props.status?.instructionKey
  if (typeof key !== 'string' || !key.includes('.')) return { exists: false, length: 0 }
  const [section, statusKey] = key.split('.')
  const html = props.workingInstructions[section]?.[statusKey]
  return { exists: !!html, length: html ? html.length : 0 }
})

const newIdError = computed(() => {
  if (props.operation === 'delete') return ''
  const value = Number(newId.value)
  if (!Number.isInteger(value) || value < 0) return t('admConfig.statusEditor.status.errId')
  if (props.operation === 'changeId' && value === Number(props.status?.id)) {
    return t('admConfig.statusEditor.impact.errSameId')
  }
  if (statusList.value.some(s => Number(s.id) === value && Number(s.id) !== Number(props.status?.id))) {
    return t('admConfig.statusEditor.status.errIdDuplicate', { id: value })
  }
  if (props.operation === 'duplicate' && value === Number(props.status?.id)) {
    return t('admConfig.statusEditor.status.errIdDuplicate', { id: value })
  }
  return ''
})

watch(() => props.modelValue, (open) => {
  if (!open) return

  // Duplicazione e cambio id partono dal primo id libero, così il campo è già valido
  const used = new Set(statusList.value.map(s => Number(s.id)))
  let candidate = Number(props.status?.id) + 1
  while (used.has(candidate)) candidate++
  newId.value = candidate

  options.value = { updateNextStates: true, updateFields: false }
  // La chiave istruzioni si rinomina o si copia di default, ma non si elimina mai:
  // una chiave orfana è inerte, un HTML cancellato si recupera solo dal backup
  instructionsOption.value = props.operation !== 'delete'
})

const confirm = () => {
  if (newIdError.value) return
  emit('confirm', {
    operation: props.operation,
    statusId: props.status.id,
    newId: props.operation === 'delete' ? null : Number(newId.value),
    options: {
      updateNextStates: options.value.updateNextStates,
      updateFields: options.value.updateFields,
      updateInstructions: instructionsOption.value,
      copyInstructions: instructionsOption.value
    }
  })
}

const cancel = (value) => {
  if (value === false || value === undefined) emit('update:modelValue', false)
}
</script>

<style scoped>
.status-swatch {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>
